/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useCallback, useMemo, useState } from "react";
import LogicContext from "../../Context/LogicContext";
import { SetParentMenu, GetParentMenu } from "../../store/parentMenuAtom";
import { useLocation, useNavigate } from "react-router-dom";
import { IoMdRefresh } from "react-icons/io";
import { moduleListService } from "../../services/module-list.service";


const ParentMenuPage = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const url = pathname.split("/");
    const currModuleId = url[1];
    const currModuleName = url[2];
    const currUserMenu = url[3];
    const { showToast } = useContext(LogicContext);

    const setParentMenuData = SetParentMenu();
    const getParentMenuData = GetParentMenu();

    const [searchText, setSearchText] = useState("");
    const [activeModuleId, setActiveModuleId] = useState("all");

    useEffect(() => {
        if (getParentMenuData?.menu_parent_info?.length > 0) return;
        getDataFromAPI();
    }, []);

    useEffect(() => {
        if (sessionStorage.getItem("reloadPage") === "true") {
            sessionStorage.removeItem("reloadPage");
            getDataFromAPI();
        }
    }, []);

    const getDataFromAPI = useCallback(async () => {
        try {
            const response = await moduleListService.getParentMenuList();
            if (response?.data) {
                setParentMenuData(response.data);
            }
        } catch (err) {
            console.error(err);
            showToast("Failed to load parent menu list", "error");
        }
    }, []);

    const moduleList = getParentMenuData?.module_info || [];
    const allMenuParents = getParentMenuData?.menu_parent_info || [];

    const filtered = useMemo(() => {
        let list = allMenuParents;
        if (activeModuleId !== "all") {
            list = list.filter(m => String(m.module_id) === String(activeModuleId));
        }
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(m =>
                m.menu_parent_name?.toLowerCase().includes(q) ||
                m.module_name?.toLowerCase().includes(q) ||
                String(m.menu_parent_id).includes(q)
            );
        }
        return list;
    }, [allMenuParents, activeModuleId, searchText]);

    // group count per module for the tab badges
    const countByModule = useMemo(() => {
        const map = {};
        allMenuParents.forEach(m => {
            map[m.module_id] = (map[m.module_id] || 0) + 1;
        });
        return map;
    }, [allMenuParents]);

    const handleRowClick = useCallback((item) => {
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${item.menu_parent_id}`);
    }, [currModuleId, currModuleName, currUserMenu, navigate]);

    const addNew = useCallback(() => {
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }, [navigate, currModuleId, currModuleName, currUserMenu]);

    return (
        <div className="p-6 bg-gray-50 min-h-screen">

            {/* ── Header ── */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Parent Menu</h2>
                    <p className="text-sm text-gray-400 mt-0.5">
                        {allMenuParents.length} menus across {moduleList.length} modules
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={getDataFromAPI}
                        className="p-2 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition"
                        title="Refresh"
                    >
                        <IoMdRefresh className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                        onClick={addNew}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-blue-700 transition"
                    >
                        + Add New
                    </button>
                </div>
            </div>

            {/* ── Module Filter Tabs ── */}
            <div className="flex flex-wrap gap-2 mb-5">
                <button
                    onClick={() => setActiveModuleId("all")}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition
                        ${activeModuleId === "all"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600"}`}
                >
                    All
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                        ${activeModuleId === "all" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                        {allMenuParents.length}
                    </span>
                </button>
                {moduleList.map(mod => (
                    <button
                        key={mod.module_id}
                        onClick={() => setActiveModuleId(String(mod.module_id))}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition
                            ${String(activeModuleId) === String(mod.module_id)
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600"}`}
                    >
                        {mod.module_name}
                        {countByModule[mod.module_id] > 0 && (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                                ${String(activeModuleId) === String(mod.module_id)
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-100 text-gray-500"}`}>
                                {countByModule[mod.module_id] || 0}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Search ── */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search by menu name, module…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    className="w-full sm:w-80 px-4 py-2 text-sm border border-gray-200 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                />
            </div>

            {/* ── Table ── */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                            <th className="px-5 py-3 text-left w-16">ID</th>
                            <th className="px-5 py-3 text-left">Menu Parent Name</th>
                            <th className="px-5 py-3 text-left">Module</th>
                            <th className="px-5 py-3 text-center w-24">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {filtered.length > 0 ? (
                            filtered.map((item, idx) => (
                                <tr
                                    key={item.menu_parent_id ?? idx}
                                    onClick={() => handleRowClick(item)}
                                    className="cursor-pointer hover:bg-blue-50/60 transition-colors group"
                                >
                                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                                        {item.menu_parent_id}
                                    </td>
                                    <td className="px-5 py-3 font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                                        {item.menu_parent_name}
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                            {item.module_name}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border
                                            ${item.status_id === 'A'
                                                ? 'bg-green-50 text-green-700 border-green-100'
                                                : 'bg-red-50 text-red-600 border-red-100'}`}>
                                            {item.status_id === 'A' ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4} className="text-center py-16 text-gray-400">
                                    <div className="flex flex-col items-center gap-2">
                                        <svg className="w-10 h-10 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h10M4 18h6" />
                                        </svg>
                                        <span className="text-sm">
                                            {searchText ? `No results for "${searchText}"` : "No menu parents found"}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {filtered.length > 0 && (
                    <div className="px-5 py-3 border-t border-gray-50 bg-gray-50 text-xs text-gray-400">
                        Showing {filtered.length} of {allMenuParents.length} entries
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(ParentMenuPage);
